import React, { useState, useEffect } from "react";
import { Container, Col, Row, Button, Form, Card, Alert, Modal } from "react-bootstrap";
import "../css/loanApplication.scss";
import { fetchRegions, fetchProvincesByRegion, fetchProvinces, fetchCities, fetchBarangays } from '../services/locationService';
import logo from "../logo.svg";
import '@fortawesome/fontawesome-free/css/all.min.css';
import axios from 'axios';
import GLOBALVAR from "../constants";

// Add these constants at the top of the file
const FORM_STORAGE_KEY = 'loanApplicationFormData';
const FORM_STATE_STORAGE_KEY = 'loanApplicationState';

function LoanApplication() {
    // Modify the initial state to check localStorage first
    const [formData, setFormData] = useState(() => {
        const savedForm = localStorage.getItem(FORM_STORAGE_KEY);
        return savedForm ? JSON.parse(savedForm) : {
            // Borrower Personal Information
            firstName: '',
            lastName: '',
            middleName: '',
            
            // Borrower Address
            province: '',
            municipality: '',
            barangay: '',
            houseStreet: '',
            zipCode: '',
            
            // Personal Details
            birthday: '',
            gender: '',
            civilStatus: '',
            
            // Contact Information
            mobileNumber1: '',
            mobileNumber2: '',
            email: '',
            
            // Employment/Income Information
            sourceOfIncome: '',
            natureOfWork: '',
            employer: '',
            jobPosition: '',
            monthlyIncome: '',
            
            // Loan Details
            loanAmount: '',
            loanTerm: '',
            loanPurpose: '',
            motherMaidenName: '',
            referredBy: '',
            
            // Spouse Information
            spouseName: '',
            spouseEmployer: '',
            spouseContact: '',
            
            // Co-maker Personal Information
            coMakerFirstName: '',
            coMakerLastName: '',
            coMakerMiddleName: '',
            
            // Co-maker Address
            coMakerProvince: '',
            coMakerMunicipality: '',
            coMakerBarangay: '',
            coMakerHouseStreet: '',
            coMakerZipCode: '',
            
            // Co-maker Contact Information
            coMakerContact: '',
            coMakerEmail: '',
            
            // Co-maker Employment/Income Information
            coMakerSourceOfIncome: '',
            coMakerNatureOfWork: '',
            coMakerEmployer: '',
            coMakerJobPosition: '',
            coMakerMonthlyIncome: ''
        };
    });

    // Add state restoration for other form states
    const [selectedProvince, setSelectedProvince] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).selectedProvince : '';
    });

    const [selectedCity, setSelectedCity] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).selectedCity : '';
    });

    const [selectedBarangay, setSelectedBarangay] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).selectedBarangay : '';
    });

    const [coMakerSelectedProvince, setCoMakerSelectedProvince] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).coMakerSelectedProvince : '';
    });

    const [coMakerSelectedCity, setCoMakerSelectedCity] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).coMakerSelectedCity : '';
    });

    const [coMakerSelectedBarangay, setCoMakerSelectedBarangay] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).coMakerSelectedBarangay : '';
    });

    const [showSpouseFields, setShowSpouseFields] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).showSpouseFields : false;
    });

    const [errors, setErrors] = useState({});
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).selectedRegion : '';
    });
    const [coMakerSelectedRegion, setCoMakerSelectedRegion] = useState(() => {
        const savedState = localStorage.getItem(FORM_STATE_STORAGE_KEY);
        return savedState ? JSON.parse(savedState).coMakerSelectedRegion : '';
    });
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [barangays, setBarangays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [coMakerProvinces, setCoMakerProvinces] = useState([]); // Added separate state for co-maker provinces
    const [coMakerCities, setCoMakerCities] = useState([]);
    const [coMakerBarangays, setCoMakerBarangays] = useState([]);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [applicationId, setApplicationId] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState('');

    useEffect(() => {
        let isMounted = true;
        const loadRegions = async () => {
            setLoading(true);
            try {
                const data = await fetchRegions();
                if (isMounted) {
                    setRegions(data);
                }
            } catch (error) {
                console.error('Error loading regions:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        loadRegions();
        
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadProvinces = async () => {
            setLoading(true);
            try {
                // If a region is selected, fetch provinces for that region
                if (selectedRegion) {
                    const data = await fetchProvincesByRegion(selectedRegion);
                    if (isMounted) {
                        setProvinces(data);
                    }
                } else {
                    // Otherwise, load all provinces
                    const data = await fetchProvinces();
                    if (isMounted) {
                        setProvinces(data);
                    }
                }
            } catch (error) {
                console.error('Error loading provinces:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        loadProvinces();
        return () => {
            isMounted = false;
        };
    }, [selectedRegion]);

    useEffect(() => {
        const loadCities = async () => {
            // Enable cities dropdown when either region or province is selected
            if (selectedProvince || selectedRegion) {
                setLoading(true);
                try {
                    let locationCode;
                    
                    if (selectedProvince) {
                        // If province is selected, prioritize that
                        locationCode = selectedRegion ? 
                            `${selectedRegion}|${selectedProvince}` : 
                            selectedProvince;
                    } else {
                        // Otherwise just use the region code
                        locationCode = selectedRegion;
                    }
                    
                    const data = await fetchCities(locationCode);
                    setCities(data);
                } catch (error) {
                    console.error('Error loading cities:', error);
                    setCities([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setCities([]);
            }
        };
        loadCities();
    }, [selectedProvince, selectedRegion]);

    useEffect(() => {
        const loadBarangays = async () => {
            if (selectedCity) {
                setLoading(true);
                try {
                    const data = await fetchBarangays(selectedCity);
                    setBarangays(data || []); // Ensure we always set an array
                } catch (error) {
                    console.error('Error loading barangays:', error);
                    setBarangays([]);
                }
                setLoading(false);
            } else {
                setBarangays([]);
            }
        };
        loadBarangays();
    }, [selectedCity]);

    useEffect(() => {
        let isMounted = true;
        const loadCoMakerProvinces = async () => {
            setLoading(true);
            try {
                // If a co-maker region is selected, fetch provinces for that region
                if (coMakerSelectedRegion) {
                    const data = await fetchProvincesByRegion(coMakerSelectedRegion);
                    if (isMounted) {
                        setCoMakerProvinces(data);
                    }
                } else {
                    // Otherwise, load all provinces
                    const data = await fetchProvinces();
                    if (isMounted) {
                        setCoMakerProvinces(data);
                    }
                }
            } catch (error) {
                console.error('Error loading co-maker provinces:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        loadCoMakerProvinces();
        return () => {
            isMounted = false;
        };
    }, [coMakerSelectedRegion]);

    useEffect(() => {
        const loadCoMakerCities = async () => {
            // Enable cities dropdown when either region or province is selected
            if (coMakerSelectedProvince || coMakerSelectedRegion) {
                setLoading(true);
                try {
                    let locationCode;
                    
                    if (coMakerSelectedProvince) {
                        // If province is selected, prioritize that
                        locationCode = coMakerSelectedRegion ? 
                            `${coMakerSelectedRegion}|${coMakerSelectedProvince}` : 
                            coMakerSelectedProvince;
                    } else {
                        // Otherwise just use the region code
                        locationCode = coMakerSelectedRegion;
                    }
                    
                    const data = await fetchCities(locationCode);
                    setCoMakerCities(data);
                } catch (error) {
                    console.error('Error loading co-maker cities:', error);
                    setCoMakerCities([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setCoMakerCities([]);
            }
        };
        loadCoMakerCities();
    }, [coMakerSelectedProvince, coMakerSelectedRegion]);

    useEffect(() => {
        const loadCoMakerBarangays = async () => {
            if (coMakerSelectedCity) {
                setLoading(true);
                try {
                    const data = await fetchBarangays(coMakerSelectedCity);
                    setCoMakerBarangays(data || []); // Ensure we always set an array
                } catch (error) {
                    console.error('Error loading co-maker barangays:', error);
                    setCoMakerBarangays([]);
                }
                setLoading(false);
            } else {
                setCoMakerBarangays([]);
            }
        };
        loadCoMakerBarangays();
    }, [coMakerSelectedCity]);

    useEffect(() => {
        if (showAlert && Object.keys(errors).length === 0) {
            setShowAlert(false);
        }
    }, [errors, showAlert]);

    // Add useEffect to save form data on change
    useEffect(() => {
        localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    }, [formData]);

    // Update the useEffect to save form state including regions
    useEffect(() => {
        const formState = {
            selectedRegion,
            selectedProvince,
            selectedCity,
            selectedBarangay,
            coMakerSelectedRegion,
            coMakerSelectedProvince,
            coMakerSelectedCity,
            coMakerSelectedBarangay,
            showSpouseFields
        };
        localStorage.setItem(FORM_STATE_STORAGE_KEY, JSON.stringify(formState));
    }, [
        selectedRegion,
        selectedProvince,
        selectedCity,
        selectedBarangay,
        coMakerSelectedRegion,
        coMakerSelectedProvince,
        coMakerSelectedCity,
        coMakerSelectedBarangay,
        showSpouseFields
    ]);

    const validatePhoneNumber = (number) => {
        // Accept formats: 09XXXXXXXXX, +639XXXXXXXXX, or +63XXXXXXXXXX
        const phoneRegex = /^(09|\+639|\+63[1-9])\d{9}$/;
        return phoneRegex.test(number);
    };

    // Update the validateEmail function
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateAge = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age >= 18;
    };

    // Update the handleInputChange function
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Show/hide spouse fields based on civil status
        if (name === 'civilStatus') {
            setShowSpouseFields(value === 'Married');
            // Clear spouse fields if not married
            if (value !== 'Married') {
                setFormData(prev => ({
                    ...prev,
                    spouseName: '',
                    spouseEmployer: '',
                    spouseContact: ''
                }));
            }
        }
    };

    const handleRegionChange = (e) => {
        const regionCode = e.target.value;
        setSelectedRegion(regionCode);
        setSelectedProvince('');
        setSelectedCity('');
        setSelectedBarangay('');
        setFormData(prev => ({
            ...prev,
            region: regions.find(r => r.code === regionCode)?.name || '',
            province: '',
            municipality: '',
            barangay: ''
        }));
    };

    const handleProvinceChange = (e) => {
        const provinceCode = e.target.value;
        setSelectedProvince(provinceCode);
        setSelectedCity('');
        setSelectedBarangay('');
        
        // Combine region and province for the backend if both are selected
        const combinedProvinceCode = selectedRegion ? `${selectedRegion}|${provinceCode}` : provinceCode;
        
        setFormData(prev => ({
            ...prev,
            province: provinces.find(p => p.code === provinceCode)?.name || '',
            municipality: '',
            barangay: ''
        }));
        
        // Store the combined code to use with fetchCities
        sessionStorage.setItem('combinedProvinceCode', combinedProvinceCode);
    };

    const handleCityChange = (e) => {
        const cityCode = e.target.value;
        setSelectedCity(cityCode);
        setSelectedBarangay('');
        setFormData(prev => ({
            ...prev,
            municipality: cities.find(c => c.code === cityCode)?.name || '',
            barangay: ''
        }));
    };

    const handleBarangayChange = (e) => {
        const barangayCode = e.target.value;
        setSelectedBarangay(barangayCode);
        setFormData(prev => ({
            ...prev,
            barangay: barangays.find(b => b.code === barangayCode)?.name || ''
        }));
    };

    const handleCoMakerRegionChange = (e) => {
        const regionCode = e.target.value;
        setCoMakerSelectedRegion(regionCode);
        setCoMakerSelectedProvince('');
        setCoMakerSelectedCity('');
        setCoMakerSelectedBarangay('');
        setFormData(prev => ({
            ...prev,
            coMakerRegion: regions.find(r => r.code === regionCode)?.name || '',
            coMakerProvince: '',
            coMakerMunicipality: '',
            coMakerBarangay: ''
        }));
    };

    const handleCoMakerProvinceChange = (e) => {
        const provinceCode = e.target.value;
        setCoMakerSelectedProvince(provinceCode);
        setCoMakerSelectedCity('');
        setCoMakerSelectedBarangay('');
        
        // Combine region and province for the backend if both are selected
        const combinedProvinceCode = coMakerSelectedRegion ? `${coMakerSelectedRegion}|${provinceCode}` : provinceCode;
        
        setFormData(prev => ({
            ...prev,
            coMakerProvince: coMakerProvinces.find(p => p.code === provinceCode)?.name || '',
            coMakerMunicipality: '',
            coMakerBarangay: ''
        }));
        
        // Store the combined code to use with fetchCities
        sessionStorage.setItem('coMakerCombinedProvinceCode', combinedProvinceCode);
    };

    const handleCoMakerCityChange = (e) => {
        const cityCode = e.target.value;
        setCoMakerSelectedCity(cityCode);
        setCoMakerSelectedBarangay('');
        setFormData(prev => ({
            ...prev,
            coMakerMunicipality: coMakerCities.find(c => c.code === cityCode)?.name || '',
            coMakerBarangay: ''
        }));
    };

    const handleCoMakerBarangayChange = (e) => {
        const barangayCode = e.target.value;
        setCoMakerSelectedBarangay(barangayCode);
        setFormData(prev => ({
            ...prev,
            coMakerBarangay: coMakerBarangays.find(b => b.code === barangayCode)?.name || ''
        }));
    };

    // Update the validateForm function to ensure it's setting errors correctly
    const validateForm = () => {
        const newErrors = {};
        let errorSummary = [];

        // Basic required fields validation
        const requiredFields = {
            firstName: 'First name',
            lastName: 'Last name',
            municipality: 'Municipality/City',
            barangay: 'Barangay',
            houseStreet: 'House/Street',
            zipCode: 'Zip code',
            birthday: 'Birthday',
            gender: 'Gender',
            civilStatus: 'Civil status',
            mobileNumber1: 'Mobile number',
            email: 'Email address',
            sourceOfIncome: 'Source of Income',
            natureOfWork: 'Nature of Work',
            employer: 'Name of Employer',
            jobPosition: 'Job Position',
            monthlyIncome: 'Monthly Net Income',
            loanAmount: 'Loan amount',
            loanTerm: 'Loan term',
            loanPurpose: 'Loan purpose',
            motherMaidenName: 'Mother\'s maiden name'
        };

        // Validate required fields
        Object.entries(requiredFields).forEach(([field, label]) => {
            if (!formData[field]) {
                newErrors[field] = `${label} is required`;
                errorSummary.push(label);
            }
        });

        // Special validation for region/province - at least one must be selected
        if (!selectedRegion && !selectedProvince) {
            newErrors.province = 'Either Region or Province must be selected';
            errorSummary.push('Region/Province');
        }

        // Validate age
        if (formData.birthday && !validateAge(formData.birthday)) {
            newErrors.birthday = 'Must be at least 18 years old';
            errorSummary.push('Age requirement');
        }

        // Validate phone numbers
        if (formData.mobileNumber1 && !validatePhoneNumber(formData.mobileNumber1)) {
            newErrors.mobileNumber1 = 'Invalid phone number format (e.g., 09XXXXXXXXX o+63939XXXXXXXXX)';
            errorSummary.push('Mobile number format');
        }

        // Update the email validation in validateForm function
        if (!formData.email) {
            newErrors.email = 'Email address is required';
            errorSummary.push('Email address');
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address (e.g., example@domain.com)';
            errorSummary.push('Invalid email format');
        }

        // Validate spouse fields if married
        if (formData.civilStatus === 'Married') {
            if (!formData.spouseName) {
                newErrors.spouseName = 'Spouse name is required';
                errorSummary.push('Spouse name');
            }
            if (!formData.spouseContact) {
                newErrors.spouseContact = 'Spouse contact is required';
                errorSummary.push('Spouse contact');
            } else if (!validatePhoneNumber(formData.spouseContact)) {
                newErrors.spouseContact = 'Invalid phone number format';
                errorSummary.push('Spouse contact format');
            }
        }

        // Validate co-maker fields
        const coMakerRequiredFields = {
            coMakerFirstName: 'Co-maker first name',
            coMakerLastName: 'Co-maker last name',
            coMakerMunicipality: 'Co-maker municipality',
            coMakerBarangay: 'Co-maker barangay',
            coMakerHouseStreet: 'Co-maker house/street',
            coMakerZipCode: 'Co-maker zip code',
            coMakerContact: 'Co-maker contact',
            coMakerEmail: 'Co-maker email',
            coMakerSourceOfIncome: 'Co-maker Source of Income',
            coMakerNatureOfWork: 'Co-maker Nature of Work',
            coMakerEmployer: 'Co-maker Name of Employer',
            coMakerJobPosition: 'Co-maker Job Position',
            coMakerMonthlyIncome: 'Co-maker Monthly Net Income'
        };

        Object.entries(coMakerRequiredFields).forEach(([field, label]) => {
            if (!formData[field]) {
                newErrors[field] = `${label} is required`;
                errorSummary.push(label);
            }
        });

        // Special validation for co-maker region/province - at least one must be selected
        if (!coMakerSelectedRegion && !coMakerSelectedProvince) {
            newErrors.coMakerProvince = 'Either Region or Province must be selected';
            errorSummary.push('Co-maker Region/Province');
        }

        // Similarly for co-maker email
        if (!formData.coMakerEmail) {
            newErrors.coMakerEmail = 'Co-maker email address is required';
            errorSummary.push('Co-maker email address');
        } else if (!validateEmail(formData.coMakerEmail)) {
            newErrors.coMakerEmail = 'Please enter a valid email address (e.g., example@domain.com)';
            errorSummary.push('Invalid co-maker email format');
        }

        // Validate co-maker contact and email
        if (formData.coMakerContact && !validatePhoneNumber(formData.coMakerContact)) {
            newErrors.coMakerContact = 'Invalid phone number format';
            errorSummary.push('Co-maker contact format');
        }
        if (formData.coMakerEmail && !validateEmail(formData.coMakerEmail)) {
            newErrors.coMakerEmail = 'Invalid email format';
            errorSummary.push('Co-maker email format');
        }

        // Set errors and show alert if there are any
        setErrors(newErrors);
        
        if (Object.keys(newErrors).length > 0) {
            setAlertMessage(`Please check the following fields: ${errorSummary.join(', ')}`);
            setShowAlert(true);
            return false;
        }

        setShowAlert(false);
        return true;
    };

    // Add this effect to handle alert visibility
    useEffect(() => {
        if (Object.keys(errors).length === 0) {
            setShowAlert(false);
        }
    }, [errors]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowConfirmModal(true);
        } else {
            setAlertMessage('Please fill in all required fields correctly.');
            setShowAlert(true);
            const firstError = document.querySelector('.is-invalid');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    const handleConfirmSubmit = async () => {
        setIsSubmitting(true);
        setShowConfirmModal(false);
        
        try {
            // First, submit the loan application
            const response = await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/loan-application/add`, {
                ...formData,
                loanAmount: formData.loanAmount ? parseFloat(formData.loanAmount).toFixed(2) : null,
                monthlyIncome: formData.monthlyIncome ? parseFloat(formData.monthlyIncome).toFixed(2) : null,
                coMakerMonthlyIncome: formData.coMakerMonthlyIncome ? parseFloat(formData.coMakerMonthlyIncome).toFixed(2) : null,
                province: (formData.region && formData.province) ? `${formData.region}, ${formData.province}` : formData.province || formData.region || '',
                coMakerProvince: (formData.coMakerRegion && formData.coMakerProvince) ? `${formData.coMakerRegion}, ${formData.coMakerProvince}` : formData.coMakerProvince || formData.coMakerRegion || ''
            });

            if (response.status === 201) {
                // Send confirmation emails
                const currentDate = new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: '2-digit'
                });

                await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/templates/send/loan_application`, {
                    recipients: [
                        {
                            email: formData.email,
                            data: {
                                applicant_name: `${formData.firstName} ${formData.lastName}`,
                                submission_date: currentDate,
                                loan_amount: parseFloat(formData.loanAmount).toLocaleString(),
                                loan_term: formData.loanTerm,
                                comaker_name: `${formData.coMakerFirstName} ${formData.coMakerLastName}`
                            }
                        },
                        {
                            email: formData.coMakerEmail,
                            data: {
                                applicant_name: `${formData.firstName} ${formData.lastName}`,
                                submission_date: currentDate,
                                loan_amount: parseFloat(formData.loanAmount).toLocaleString(),
                                loan_term: formData.loanTerm,
                                comaker_name: `${formData.coMakerFirstName} ${formData.coMakerLastName}`
                            }
                        }
                    ]
                });

                // Clear localStorage upon successful submission
                localStorage.removeItem(FORM_STORAGE_KEY);
                localStorage.removeItem(FORM_STATE_STORAGE_KEY);

                setSubmitSuccess(true);
                const loansapp = response.data.application.id.toString().padStart(6, '0');
                setApplicationId(loansapp);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Submission error:', error);

            const errorMessage = error.response?.data?.message || 
                'An error occurred while submitting the form. Please try again.';

            setAlertMessage(errorMessage);
            setShowAlert(true);
            setShowErrorModal(true);
            setErrorModalMessage(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };    

    // Add a function to clear saved data (optional)
    const clearSavedData = () => {
        localStorage.removeItem(FORM_STORAGE_KEY);
        localStorage.removeItem(FORM_STATE_STORAGE_KEY);
    };

    // Add an unload warning
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!submitSuccess && Object.keys(formData).some(key => formData[key])) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [formData, submitSuccess]);

    const ConfirmationModal = () => (
        <Modal
            show={showConfirmModal}
            onHide={() => setShowConfirmModal(false)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Confirm Submission</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Please confirm that all the information provided is accurate and true.</p>
                <p className="text-muted small">
                    Note: Submitting false information may result in the rejection of your application.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                    Review Application
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirmSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Submitting...' : 'Confirm & Submit'}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    const SuccessView = () => (
        <div className="text-center success-container">
            <img src={logo} alt="logo" className="logo-application mb-4" />
            <div className="success-content">
                <div className="success-icon mb-4">
                    <i className="fas fa-check-circle"></i>
                </div>
                <h2 className="mb-3">Application Submitted Successfully!</h2>
                <p className="mb-4">
                    Thank you for submitting your loan application. Your application ID is:{' '}
                    <strong>{applicationId}</strong>
                </p>
                <div className="info-box p-4 mb-4">
                    <h5>What's Next?</h5>
                    <p>Our staff will review your application and reach out to you within the next 24 hours.</p>
                    <p className="mb-0">
                        For any urgent inquiries, please contact our support team at:{' '}
                        <a href="mailto:info@hayahaycorp.com">info@hayahaycorp.com</a>
                    </p>
                </div>
            </div>
        </div>
    );

    const ClearDataButton = ({ onClear }) => (
        <Button 
            variant="link" 
            className="text-danger small" 
            onClick={() => {
                if (window.confirm('Are you sure you want to clear all saved data?')) {
                    onClear();
                    window.location.reload();
                }
            }}
        >
            Clear saved form data
        </Button>
    );

    return (
        <>
            {/* Loading Overlay */}
            {isSubmitting && (
                <div className="loading-overlay">
                    <div className="loading-container">
                        <div className="spinner-border text-light" role="status">
                            <span className="visually-hidden">Submitting...</span>
                        </div>
                        <div className="loading-text">Submitting your application...</div>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered className="modal-error">
                <Modal.Header closeButton>
                    <Modal.Title>Submission Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{errorModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Container fluid className="loan-application-container">
                <Row className="justify-content-center align-items-center h-100">
                    <Col xs={12} sm={11} md={10} lg={12} xl={12}>
                        <Card className="loan-application-card">
                            <Card.Body className="text-center">
                                {submitSuccess ? (
                                    <SuccessView />
                                ) : (
                                    <>
                                        {showAlert && (
                                            <Alert 
                                                variant="danger" 
                                                onClose={() => setShowAlert(false)} 
                                                dismissible
                                                className="mb-4"
                                            >
                                                <Alert.Heading>Form Validation Error</Alert.Heading>
                                                <p>{alertMessage}</p>
                                                <hr />
                                                <p className="mb-0">
                                                    Fields marked with * are required.
                                                </p>
                                            </Alert>
                                        )}
                                        <img src={logo} alt="logo" className="logo-application" />
                                        <h2>Loan Application Form</h2>
                                        {Object.keys(formData).some(key => formData[key]) && (
                                            <div className="text-end mb-3">
                                                <ClearDataButton onClear={clearSavedData} />
                                            </div>
                                        )}
                                        <Form onSubmit={handleSubmit} className="text-start" noValidate>
                                            {/* Borrower Details Section */}
                                            <h3 className="mb-3">Borrower Details</h3>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>First Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="firstName"
                                                        value={formData.firstName}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.firstName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.firstName}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Last Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="lastName"
                                                        value={formData.lastName}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.lastName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lastName}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Middle Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="middleName"
                                                        value={formData.middleName}
                                                        onChange={handleInputChange}
                                                    />
                                                </Form.Group>
                                            </Row>

                                            {/* Address Section */}
                                            <h4 className="mb-3">Address</h4>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Region</Form.Label>
                                                    <Form.Select
                                                        value={selectedRegion}
                                                        onChange={handleRegionChange}
                                                        disabled={loading}
                                                    >
                                                        <option value="">Select Region</option>
                                                        {regions.map(region => (
                                                            <option key={region.code} value={region.code}>
                                                                {region.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading regions...</div>}
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Province *</Form.Label>
                                                    <Form.Select
                                                        value={selectedProvince}
                                                        onChange={handleProvinceChange}
                                                        isInvalid={!!errors.province}
                                                        disabled={loading}
                                                    >
                                                        <option value="">Select Province</option>
                                                        {provinces.map(province => (
                                                            <option key={province.code} value={province.code}>
                                                                {province.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading provinces...</div>}
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.province}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>City/Municipality *</Form.Label>
                                                    <Form.Select
                                                        value={selectedCity}
                                                        onChange={handleCityChange}
                                                        isInvalid={!!errors.municipality}
                                                        disabled={!(selectedProvince || selectedRegion) || loading}
                                                    >
                                                        <option value="">Select City/Municipality</option>
                                                        {cities.map(city => (
                                                            <option key={city.code} value={city.code}>
                                                                {city.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading cities...</div>}
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.municipality}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Barangay *</Form.Label>
                                                    <Form.Select
                                                        value={selectedBarangay}
                                                        onChange={handleBarangayChange}
                                                        isInvalid={!!errors.barangay}
                                                        disabled={!selectedCity || loading}
                                                    >
                                                        <option value="">Select Barangay</option>
                                                        {barangays.map(barangay => (
                                                            <option key={barangay.code} value={barangay.code}>
                                                                {barangay.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading barangays...</div>}
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.barangay}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="8">
                                                    <Form.Label>House No. / Street *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="houseStreet"
                                                        value={formData.houseStreet}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.houseStreet}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.houseStreet}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Zip Code *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="zipCode"
                                                        value={formData.zipCode}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.zipCode}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.zipCode}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            {/* Personal Information */}
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Birthday *</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="birthday"
                                                        value={formData.birthday}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.birthday}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.birthday}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Gender *</Form.Label>
                                                    <Form.Select
                                                        name="gender"
                                                        value={formData.gender}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.gender}
                                                    >
                                                        <option value="">Select Gender</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.gender}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Civil Status *</Form.Label>
                                                    <Form.Select
                                                        name="civilStatus"
                                                        value={formData.civilStatus}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.civilStatus}
                                                    >
                                                        <option value="">Select Civil Status</option>
                                                        <option value="Single">Single</option>
                                                        <option value="Married">Married</option>
                                                        <option value="Widowed">Widowed</option>
                                                        <option value="Separated">Separated</option>
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.civilStatus}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Mobile Number 1 *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="mobileNumber1"
                                                        value={formData.mobileNumber1}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.mobileNumber1}
                                                        placeholder="09XXXXXXXXX"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.mobileNumber1}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Mobile Number 2</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="mobileNumber2"
                                                        value={formData.mobileNumber2}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.mobileNumber2}
                                                        placeholder="09XXXXXXXXX"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.mobileNumber2}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Email Address *</Form.Label>
                                                    <Form.Control
                                                        type="text" // Change from type="email" to type="text"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.email}
                                                        placeholder="example@domain.com"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.email}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Source of Income *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="sourceOfIncome"
                                                        value={formData.sourceOfIncome}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.sourceOfIncome}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.sourceOfIncome}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Nature of Work *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="natureOfWork"
                                                        value={formData.natureOfWork}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.natureOfWork}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.natureOfWork}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Name of Employer *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="employer"
                                                        value={formData.employer}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.employer}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.employer}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Job Position *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="jobPosition"
                                                        value={formData.jobPosition}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.jobPosition}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.jobPosition}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Monthly Net Income *</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="monthlyIncome"
                                                        value={formData.monthlyIncome}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.monthlyIncome}
                                                        placeholder="₱0.00"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.monthlyIncome}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Loan Amount *</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="loanAmount"
                                                        value={formData.loanAmount}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.loanAmount}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.loanAmount}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Loan Term *</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="loanTerm"
                                                        value={formData.loanTerm}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.loanTerm}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.loanTerm}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="3">
                                                    <Form.Label>Loan Purpose *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="loanPurpose"
                                                        value={formData.loanPurpose}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.loanPurpose}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.loanPurpose}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="6">
                                                    <Form.Label>Mother's Maiden Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="motherMaidenName"
                                                        value={formData.motherMaidenName}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.motherMaidenName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.motherMaidenName}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="6">
                                                    <Form.Label>Referred by</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="referredBy"
                                                        value={formData.referredBy}
                                                        onChange={handleInputChange}
                                                    />
                                                </Form.Group>
                                            </Row>

                                            {/* Spouse Information (Conditional) */}
                                            {showSpouseFields && (
                                                <>
                                                    <h4 className="mb-3">Spouse Information</h4>
                                                    <Row className="mb-3">
                                                        <Form.Group as={Col} md="4">
                                                            <Form.Label>Spouse Name *</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="spouseName"
                                                                value={formData.spouseName}
                                                                onChange={handleInputChange}
                                                                isInvalid={!!errors.spouseName}
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                {errors.spouseName}
                                                            </Form.Control.Feedback>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md="4">
                                                            <Form.Label>Spouse Employer</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="spouseEmployer"
                                                                value={formData.spouseEmployer}
                                                                onChange={handleInputChange}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md="4">
                                                            <Form.Label>Spouse Contact Number *</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="spouseContact"
                                                                value={formData.spouseContact}
                                                                onChange={handleInputChange}
                                                                isInvalid={!!errors.spouseContact}
                                                                placeholder="09XXXXXXXXX"
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                {errors.spouseContact}
                                                            </Form.Control.Feedback>
                                                        </Form.Group>
                                                    </Row>
                                                </>
                                            )}

                                            {/* Co-Maker Information */}
                                            <h3 className="mb-3">Co-Maker Information</h3>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>First Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerFirstName"
                                                        value={formData.coMakerFirstName}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerFirstName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerFirstName}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Last Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerLastName"
                                                        value={formData.coMakerLastName}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerLastName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerLastName}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Middle Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerMiddleName"
                                                        value={formData.coMakerMiddleName}
                                                        onChange={handleInputChange}
                                                    />
                                                </Form.Group>
                                            </Row>

                                            <h4 className="mb-3">Co-Maker Address</h4>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Region</Form.Label>
                                                    <Form.Select
                                                        value={coMakerSelectedRegion}
                                                        onChange={handleCoMakerRegionChange}
                                                        disabled={loading}
                                                    >
                                                        <option value="">Select Region</option>
                                                        {regions.map(region => (
                                                            <option key={region.code} value={region.code}>
                                                                {region.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading regions...</div>}
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Province *</Form.Label>
                                                    <Form.Select
                                                        value={coMakerSelectedProvince}
                                                        onChange={handleCoMakerProvinceChange}
                                                        isInvalid={!!errors.coMakerProvince}
                                                        disabled={loading}
                                                    >
                                                        <option value="">Select Province</option>
                                                        {coMakerProvinces.map(province => (
                                                            <option key={province.code} value={province.code}>
                                                                {province.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading provinces...</div>}
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerProvince}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>City/Municipality *</Form.Label>
                                                    <Form.Select
                                                        value={coMakerSelectedCity}
                                                        onChange={handleCoMakerCityChange}
                                                        isInvalid={!!errors.coMakerMunicipality}
                                                        disabled={!(coMakerSelectedProvince || coMakerSelectedRegion) || loading}
                                                    >
                                                        <option value="">Select City/Municipality</option>
                                                        {coMakerCities.map(city => (
                                                            <option key={city.code} value={city.code}>
                                                                {city.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading cities...</div>}
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerMunicipality}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Barangay *</Form.Label>
                                                    <Form.Select
                                                        value={coMakerSelectedBarangay}
                                                        onChange={handleCoMakerBarangayChange}
                                                        isInvalid={!!errors.coMakerBarangay}
                                                        disabled={!coMakerSelectedCity || loading}
                                                    >
                                                        <option value="">Select Barangay</option>
                                                        {coMakerBarangays.map(barangay => (
                                                            <option key={barangay.code} value={barangay.code}>
                                                                {barangay.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {loading && <div className="text-muted small">Loading barangays...</div>}
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerBarangay}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="8">
                                                    <Form.Label>House No. / Street *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerHouseStreet"
                                                        value={formData.coMakerHouseStreet}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerHouseStreet}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerHouseStreet}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Zip Code *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerZipCode"
                                                        value={formData.coMakerZipCode}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerZipCode}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerZipCode}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="6">
                                                    <Form.Label>Contact Number *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerContact"
                                                        value={formData.coMakerContact}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerContact}
                                                        placeholder="09XXXXXXXXX"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerContact}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="6">
                                                    <Form.Label>Email Address *</Form.Label>
                                                    <Form.Control
                                                        type="text" // Change from type="email" to type="text"
                                                        name="coMakerEmail"
                                                        value={formData.coMakerEmail}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerEmail}
                                                        placeholder="example@domain.com"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerEmail}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <h4 className="mb-3">Co-Maker Employment Information</h4>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Source of Income *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerSourceOfIncome"
                                                        value={formData.coMakerSourceOfIncome}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerSourceOfIncome}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerSourceOfIncome}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Nature of Work *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerNatureOfWork"
                                                        value={formData.coMakerNatureOfWork}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerNatureOfWork}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerNatureOfWork}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="4">
                                                    <Form.Label>Name of Employer *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerEmployer"
                                                        value={formData.coMakerEmployer}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerEmployer}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerEmployer}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>
                                            <Row className="mb-3">
                                                <Form.Group as={Col} md="6">
                                                    <Form.Label>Job Position *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="coMakerJobPosition"
                                                        value={formData.coMakerJobPosition}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerJobPosition}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerJobPosition}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <Form.Group as={Col} md="6">
                                                    <Form.Label>Monthly Net Income *</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="coMakerMonthlyIncome"
                                                        value={formData.coMakerMonthlyIncome}
                                                        onChange={handleInputChange}
                                                        isInvalid={!!errors.coMakerMonthlyIncome}
                                                        placeholder="₱0.00"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.coMakerMonthlyIncome}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <Button 
                                                variant="primary" 
                                                type="submit" 
                                                className="mt-3" 
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Submitting...' : 'Submit Application'}
                                            </Button>
                                            <ClearDataButton onClear={clearSavedData} />
                                        </Form>
                                        <ConfirmationModal />
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return <h1>Something went wrong. Please refresh the page.</h1>;
        }
        return this.props.children;
    }
}

export default function WrappedLoanApplication() {
    return (
        <ErrorBoundary>
            <LoanApplication />
        </ErrorBoundary>
    );
}
